import { Injectable, Optional, Inject } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { DateTime, WeekdayNumbers } from 'luxon';

@Injectable()
export class LuxonDateAdapter extends DateAdapter<DateTime> {
    constructor(
        @Optional()
        @Inject(MAT_DATE_LOCALE)
        matDateLocale: string
    ) {
        super();
        this.setLocale(matDateLocale || 'pt-BR');
    }

    override setLocale(locale: string): void {
        super.setLocale(locale);
    }

    override getYear(date: DateTime): number {
        return date.year;
    }

    override getMonth(date: DateTime): number {
        return date.month - 1;
    }

    override getDate(date: DateTime): number {
        return date.day;
    }

    override getDayOfWeek(date: DateTime): number {
        return date.weekday % 7;
    }

    override getMonthNames(): string[] {
        return Array.from({ length: 12 }, (_, i) =>
            DateTime.local(2000, i + 1, 1).setLocale(this.locale).toFormat('LLLL')
        );
    }

    override getDateNames(): string[] {
        return Array.from({ length: 31 }, (_, i) => String(i + 1));
    }

    override getDayOfWeekNames(): string[] {
        return Array.from({ length: 7 }, (_, i) =>
            DateTime.local().set({ weekday: i as WeekdayNumbers }).setLocale(this.locale).toFormat('ccc')
        );
    }

    override getFirstDayOfWeek(): number {
        return 0; // segunda-feira
    }

    override getNumDaysInMonth(date: DateTime): number {
        return date.daysInMonth;
    }

    override clone(date: DateTime): DateTime {
        return date;
    }

    override createDate(year: number, month: number, date: number): DateTime {
        return DateTime.local(year, month + 1, date, 0, 0, 0, 0)
            .setLocale(this.locale)
            .setZone('local', { keepLocalTime: true });
    }

    override today(): DateTime {
        return DateTime.local().startOf('day')
            .setLocale(this.locale)
            .setZone('local', { keepLocalTime: true });
    }

    override parse(value: any): DateTime | null {
        if (!value) return null;
        if (typeof value === 'string') {
            const parsed = DateTime.fromFormat(value, 'dd/MM/yyyy', { locale: this.locale });
            return parsed.isValid ? parsed : null;
        }
        return value;
    }

    override format(date: DateTime, displayFormat: string): string {
        return date.setLocale(this.locale).toFormat(displayFormat);
    }

    override toIso8601(date: DateTime): string {
        return date.toISODate();
    }

    override isDateInstance(obj: any): boolean {
        return DateTime.isDateTime(obj);
    }

    override isValid(date: DateTime): boolean {
        return date.isValid;
    }

    override invalid(): DateTime {
        return DateTime.invalid('Invalid');
    }

    override getYearName(date: DateTime): string {
        return date.setLocale(this.locale).toFormat('yyyy');
    }

    override addCalendarYears(date: DateTime, years: number): DateTime {
        return date.plus({ years });
    }

    override addCalendarMonths(date: DateTime, months: number): DateTime {
        return date.plus({ months });
    }

    override addCalendarDays(date: DateTime, days: number): DateTime {
        return date.plus({ days });
    }

}
